import { readAnalysisEnabled } from '../../core/flags.js';
import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { resolveChatModelPreselect } from '../settings/util.js';
import { mentionChoices, applyMention } from './mentions.js';
import { sendTyping } from './socket.js';

function svg(path) {
  return el('svg', {
    viewBox: '0 0 24 24',
    width: '16',
    height: '16',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '1.75',
    'stroke-linecap': 'square',
    'aria-hidden': 'true',
  }, el('path', { d: path }));
}

function readLocalRole() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : '';
    const user = raw ? JSON.parse(raw) : null;
    return user && user.role ? String(user.role) : '';
  } catch {
    return '';
  }
}

/**
 * @param {{
 *   groupId?: string,
 *   productId?: string,
 *   placeholder: string,
 *   members?: object[],
 *   me?: object,
 *   analysisEnabled?: boolean,
 *   onSend: Function,
 *   onDeepStarted?: Function,
 * }} opts
 */
export function mountComposer(opts) {
  const files = [];
  let members = opts.members || [];
  let lastTyping = 0;
  let deepOn = false;
  let mode = 'chat';
  let language = 'en';
  let preferredLanguage = 'en';
  let preferredLabel = 'English';
  let deepBusy = false;

  const input = el('textarea', {
    class: 'composer-input',
    rows: '2',
    placeholder: opts.placeholder,
    'aria-label': 'Message',
  });
  const fileInput = el('input', { type: 'file', multiple: 'true', hidden: true, 'aria-label': 'Attach files' });
  const chips = el('div', { class: 'composer-files' });
  const menu = el('div', { class: 'mention-menu', role: 'listbox', hidden: true });
  const model = el('select', { class: 'model-select', 'aria-label': 'Model' },
    el('option', { value: 'auto' }, 'Model: Auto'));
  const send = el('button', { type: 'submit', class: 'send-btn', 'aria-label': 'Send' },
    svg('M12 19V5M6 11l6-6 6 6'));
  const deepPanel = el('div', { class: 'composer-deep', hidden: true });
  const deepError = el('p', { class: 'composer-deep-error', hidden: true, role: 'alert' });
  const deepToggle = el('button', {
    type: 'button',
    class: 'tool-btn composer-deep-toggle',
    'aria-pressed': 'false',
    hidden: true,
  }, 'Deep analysis');

  const form = el('form', { class: 'composer' },
    menu,
    chips,
    deepPanel,
    deepError,
    input,
    el('div', { class: 'composer-tools' },
      el('button', { type: 'button', class: 'tool-btn', onClick: () => fileInput.click() },
        svg('M20 11l-8.5 8.5a5 5 0 0 1-7-7L13 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L14 7'),
        'Attach'),
      model,
      el('button', {
        type: 'button',
        class: 'tool-btn',
        onClick: () => {
          const needsSpace = input.value && !/\s$/.test(input.value);
          input.value += `${needsSpace ? ' ' : ''}@`;
          input.focus();
          showMentions();
        },
      }, '@ Mention'),
      deepToggle,
      el('span', { class: 'composer-hint' }, 'Mentions notify · attachments save to Knowledge base · credentials blocked'),
      send),
    fileInput);

  function paintFiles() {
    clear(chips);
    chips.hidden = files.length === 0;
    files.forEach((file, index) => {
      chips.append(el('span', { class: 'file-chip' },
        file.name,
        el('button', {
          type: 'button',
          class: 'chip-x',
          'aria-label': `Remove ${file.name}`,
          onClick: () => { files.splice(index, 1); paintFiles(); },
        }, '×')));
    });
  }

  function showMentions() {
    const caret = input.selectionStart || 0;
    const match = input.value.slice(0, caret).match(/@([A-Za-z0-9._-]*)$/);
    clear(menu);
    if (!match) {
      menu.hidden = true;
      return;
    }
    const hits = mentionChoices(members, match[1]);
    if (!hits.length) {
      menu.hidden = true;
      return;
    }
    menu.hidden = false;
    hits.forEach((hit, index) => {
      menu.append(el('button', {
        type: 'button',
        class: index === 0 ? 'mention-hit is-on' : 'mention-hit',
        role: 'option',
        onClick: () => choose(hit.name),
      }, hit.agent ? `${hit.name} · AI` : hit.name));
    });
  }

  function choose(name) {
    input.value = applyMention(input.value, input.selectionStart, name);
    menu.hidden = true;
    input.focus();
  }

  function chosenModel() {
    return model.value || 'auto';
  }

  function setDeepError(text) {
    deepError.hidden = !text;
    deepError.textContent = text || '';
  }

  function paintDeepPanel() {
    clear(deepPanel);
    if (!deepOn) {
      deepPanel.hidden = true;
      return;
    }
    deepPanel.hidden = false;
    const modeRow = el('div', {
      class: 'composer-deep-modes',
      role: 'group',
      'aria-label': 'Deep analysis mode',
    });
    const chatBtn = el('button', {
      type: 'button',
      class: mode === 'chat' ? 'tool-btn is-on' : 'tool-btn',
      'aria-pressed': mode === 'chat' ? 'true' : 'false',
      onClick: () => { mode = 'chat'; paintDeepPanel(); },
    }, 'In chat');
    const reportBtn = el('button', {
      type: 'button',
      class: mode === 'report' ? 'tool-btn is-on' : 'tool-btn',
      'aria-pressed': mode === 'report' ? 'true' : 'false',
      onClick: () => { mode = 'report'; paintDeepPanel(); },
    }, 'Full report');
    modeRow.append(chatBtn, reportBtn);
    deepPanel.append(modeRow);

    if (preferredLanguage && preferredLanguage !== 'en') {
      const langRow = el('div', {
        class: 'composer-deep-lang',
        role: 'group',
        'aria-label': 'Report language',
      });
      const prefBtn = el('button', {
        type: 'button',
        class: language === preferredLanguage ? 'tool-btn is-on' : 'tool-btn',
        'aria-pressed': language === preferredLanguage ? 'true' : 'false',
        onClick: () => { language = preferredLanguage; paintDeepPanel(); },
      }, preferredLabel || preferredLanguage);
      const enBtn = el('button', {
        type: 'button',
        class: language === 'en' ? 'tool-btn is-on' : 'tool-btn',
        'aria-pressed': language === 'en' ? 'true' : 'false',
        onClick: () => { language = 'en'; paintDeepPanel(); },
      }, 'English');
      langRow.append(
        el('span', { class: 'composer-deep-lang-label' }, 'Report language:'),
        prefBtn,
        enBtn
      );
      deepPanel.append(langRow);
    }
  }

  function canShowDeepToggle() {
    const role = (opts.me && opts.me.role) || readLocalRole();
    if (role !== 'admin') return false;
    if (typeof opts.analysisEnabled === 'boolean') return opts.analysisEnabled;
    // Hidden unless /auth/me says analysis is on for this tenant.
    return readAnalysisEnabled(opts.me);
  }

  function refreshDeepToggle() {
    const show = canShowDeepToggle();
    deepToggle.hidden = !show;
    if (!show) {
      deepOn = false;
      deepToggle.setAttribute('aria-pressed', 'false');
      deepToggle.classList.remove('is-on');
      paintDeepPanel();
      setDeepError('');
    }
  }

  deepToggle.addEventListener('click', () => {
    deepOn = !deepOn;
    deepToggle.setAttribute('aria-pressed', deepOn ? 'true' : 'false');
    deepToggle.classList.toggle('is-on', deepOn);
    setDeepError('');
    paintDeepPanel();
  });

  async function sendDeep(text) {
    const groupId = opts.groupId;
    if (!groupId) {
      setDeepError('Open a chat first.');
      return false;
    }
    if (text.trim().length < 5) {
      setDeepError('Ask a longer question (at least 5 characters).');
      return false;
    }
    deepBusy = true;
    send.disabled = true;
    setDeepError('');

    const msgRes = await api(`/chat/${encodeURIComponent(groupId)}/messages`, {
      method: 'POST',
      body: {
        content: text,
        deep: true,
        model_id: chosenModel() === 'auto' ? undefined : chosenModel(),
      },
    });
    if (!msgRes || msgRes.error || !msgRes.message) {
      deepBusy = false;
      send.disabled = false;
      if (msgRes && msgRes.status === 403) {
        setDeepError('Not allowed.');
      } else if (msgRes && msgRes.status === 409 && msgRes.run_id) {
        // An active run in this chat: say so and bring its panel into view.
        setDeepError(msgRes.error);
        const panel = typeof document !== 'undefined'
          ? document.getElementById(`analysis-run-${msgRes.run_id}`)
          : null;
        if (panel && typeof panel.scrollIntoView === 'function') {
          panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } else {
        setDeepError((msgRes && msgRes.error) || 'Could not send.');
      }
      return false;
    }

    const body = {
      chat_group_id: groupId,
      question: text,
      mode: mode === 'report' ? 'report' : 'chat',
      language,
      trigger_message_id: msgRes.message.id,
    };
    // Prefer the chat group's product (server falls back when omitted).
    if (opts.productId) body.product_id = opts.productId;

    const runRes = await api('/analysis', { method: 'POST', body });
    deepBusy = false;
    send.disabled = false;

    if (!runRes || runRes.error) {
      const status = runRes && runRes.status;
      if (status === 403) setDeepError('Not allowed.');
      else if (status === 404) setDeepError('Deep analysis is not enabled.');
      else setDeepError((runRes && runRes.error) || 'Could not start analysis.');
      if (typeof opts.onDeepStarted === 'function') {
        opts.onDeepStarted({ message: msgRes.message, run: null, error: runRes });
      }
      return false;
    }

    setDeepError('');
    if (typeof opts.onDeepStarted === 'function') {
      opts.onDeepStarted({ message: msgRes.message, run: runRes.run || runRes });
    }
    return true;
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (deepBusy) return;
    const text = input.value.trim();
    if (!text && !files.length) return;

    if (deepOn && canShowDeepToggle()) {
      const draft = { text: input.value, files: files.slice() };
      input.value = '';
      files.length = 0;
      paintFiles();
      menu.hidden = true;
      const ok = await sendDeep(draft.text);
      if (!ok) {
        input.value = draft.text || '';
        files.length = 0;
        for (const file of draft.files || []) files.push(file);
        paintFiles();
      }
      return;
    }

    opts.onSend({ text, files: files.slice(), modelId: chosenModel(), deep: false });
  });
  input.addEventListener('keydown', (event) => {
    if (!menu.hidden && (event.key === 'Enter' || event.key === 'Tab')) {
      const first = menu.querySelector('.mention-hit');
      if (first) {
        event.preventDefault();
        first.click();
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      form.requestSubmit();
    }
  });
  input.addEventListener('input', () => {
    showMentions();
    const now = Date.now();
    if (opts.groupId && now - lastTyping > 2000) {
      lastTyping = now;
      sendTyping(opts.groupId);
    }
  });
  fileInput.addEventListener('change', () => {
    for (const file of fileInput.files || []) files.push(file);
    fileInput.value = '';
    paintFiles();
  });
  form.addEventListener('dragover', (event) => event.preventDefault());
  form.addEventListener('drop', (event) => {
    event.preventDefault();
    for (const file of event.dataTransfer ? event.dataTransfer.files : []) files.push(file);
    paintFiles();
  });
  model.addEventListener('change', () => {
    try { localStorage.setItem('chatModelId', chosenModel()); } catch { /* ignore */ }
  });
  paintFiles();
  refreshDeepToggle();
  loadModels(model);
  loadDeepPrefs();

  async function loadDeepPrefs() {
    if (!canShowDeepToggle()) return;
    const data = await api('/workspace/settings');
    if (!data || data.error) return;
    preferredLanguage = data.chat_language || 'en';
    preferredLabel = (data.language && (data.language.nativeLabel || data.language.label))
      || preferredLanguage;
    language = preferredLanguage;
    if (deepOn) paintDeepPanel();
  }

  return {
    node: form,
    setMembers(next) { members = next || []; },
    setGroup(id) { opts.groupId = id; },
    setProductId(id) { opts.productId = id || ''; },
    setAnalysisEnabled(on) {
      opts.analysisEnabled = !!on;
      refreshDeepToggle();
    },
    setBusy(busy) {
      if (deepBusy) return;
      send.disabled = !!busy;
      form.setAttribute('aria-busy', busy ? 'true' : 'false');
    },
    prefill(text) {
      input.value = text;
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    },
    takeDraft() {
      const draft = { text: input.value, files: files.slice() };
      input.value = '';
      files.length = 0;
      paintFiles();
      menu.hidden = true;
      return draft;
    },
    restore(draft) {
      input.value = draft.text || '';
      files.length = 0;
      for (const file of draft.files || []) files.push(file);
      paintFiles();
    },
    focus() { input.focus(); },
    setPlaceholder(text) { input.placeholder = text; },
    /** @returns {{ deepOn: boolean, mode: string, language: string }} */
    deepState() { return { deepOn, mode, language }; },
  };
}

async function loadModels(select) {
  const data = await api('/models');
  const models = (data && data.models) || [];
  for (const item of models) {
    if (item.enabled === false) continue;
    select.append(el('option', { value: item.id }, item.label || item.id));
  }
  let saved = '';
  try { saved = localStorage.getItem('chatModelId') || ''; } catch { /* ignore */ }
  select.value = resolveChatModelPreselect({
    saved,
    defaultModelId: data && data.default_model_id,
    optionValues: [...select.options].map((option) => option.value),
  });
}
